import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaChevronLeft, FaChevronRight, FaUndo, FaSearchPlus, FaSearchMinus, FaBars, FaSave, FaSpellCheck, FaQuestionCircle } from 'react-icons/fa';
import * as pdfjsLib from 'pdfjs-dist';
import { fetchPdfUrl } from '../../services/pptService';
import { auth } from '../../firebase';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const TypoResultModal = ({
  isOpen,
  onClose,
  pdfUrl: pdfUrlProp,
  pdfDoc: cachedPdfDoc,
  setPdfDoc: setCachedPdfDoc,
  slides,
  slideTypoResults,
  currentSlideIndex,
  goToSlide,
  selectedSlideTypos,
  toggleSlideTypoSelection,
  updateEditedSuggestion,
  getEditedSuggestion,
  editedSuggestions, // 전체 편집된 제안들
  applySlideTypoCorrections,
  applyingTypoCorrections,
  isMobile = false,
  selectedPpt,
  slideSize,
  onSaveDraft, // 임시저장 함수
  checkSlideTypos, // 개별 슬라이드 오타검사 함수
  slideTypoCheckStatus, // 슬라이드별 검사 상태
  initialState = { selectedTypos: {}, editedSuggestions: {} } // 초기 상태 (모달 열 때의 상태)
}) => {
  const { t } = useTranslation();

  // Refs
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isRenderingRef = useRef(false);
  const highlightTimerRef = useRef(null);
  const scaleRef = useRef(1.5);
  const lastRenderedPageRef = useRef(null);
  const renderingPageRef = useRef(null);
  const zoomContainerRef = useRef(null);

  // States
  const [localPdfDoc, setLocalPdfDoc] = useState(null);
  const [localPdfUrl, setLocalPdfUrl] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [, setScale] = useState(1.5);
  const [textItems, setTextItems] = useState([]);
  const [currentViewport, setCurrentViewport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isContainerReady, setIsContainerReady] = useState(false);
  const [highlightRects, setHighlightRects] = useState([]);
  const [selectedHighlights, setSelectedHighlights] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showMobileSidebar, setShowMobileSidebar] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);

  const pdfDoc = cachedPdfDoc || localPdfDoc;
  const _pdfUrl = pdfUrlProp || localPdfUrl; // eslint-disable-line no-unused-vars

  // 줌 관련 상수
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 2;
  const ZOOM_STEP = 0.25;

  // 하이라이트 관련 상수
  const HIGHLIGHT_PADDING_START = 1.5; // 텍스트 검색 시 앞 여유 글자 수
  const HIGHLIGHT_PADDING_END = 0.5;   // 텍스트 검색 시 뒤 여유 글자 수
  const HIGHLIGHT_EXPAND_RATIO = 0.2;  // 빨간 사각형 확장 비율 (20%)

  // Memoized values
  const currentTypos = useMemo(() => slideTypoResults[currentSlideIndex] || [], [slideTypoResults, currentSlideIndex]);
  const selectedTypos = useMemo(() => selectedSlideTypos[currentSlideIndex] || [], [selectedSlideTypos, currentSlideIndex]);

  // 빈 배열을 제거한 selectedTypos 반환 (실제 선택된 것만 비교용)
  const filterEmptySelections = useMemo(() => {
    return (selectedTypos) => {
      if (!selectedTypos) return {};
      const filtered = {};
      Object.entries(selectedTypos).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          filtered[key] = value;
        }
      });
      return filtered;
    };
  }, []);

  // 초기 상태와 비교하여 변경사항이 있는지 계산
  const hasUnsavedChanges = useMemo(() => {
    const filteredInitialTypos = filterEmptySelections(initialState?.selectedTypos);
    const filteredCurrentTypos = filterEmptySelections(selectedSlideTypos);

    const hasSelectedChanges = JSON.stringify(filteredInitialTypos) !== JSON.stringify(filteredCurrentTypos);
    const hasEditedChanges = JSON.stringify(initialState?.editedSuggestions || {}) !== JSON.stringify(editedSuggestions || {});

    return hasSelectedChanges || hasEditedChanges;
  }, [initialState, selectedSlideTypos, editedSuggestions, filterEmptySelections]);

  // ========== 유틸리티 함수 ==========

  // 텍스트 아이템을 줄별로 그룹화
  const groupTextItemsByLine = useCallback((items, yThreshold = 10) => {
    const lineGroups = [];
    let currentLine = [];
    let lastY = null;

    items.forEach((item) => {
      const y = item.transform[5];
      if (lastY === null || Math.abs(y - lastY) < yThreshold) {
        currentLine.push(item);
      } else {
        if (currentLine.length > 0) lineGroups.push(currentLine);
        currentLine = [item];
      }
      lastY = y;
    });
    if (currentLine.length > 0) lineGroups.push(currentLine);

    return lineGroups;
  }, []);

  // EMU 좌표를 PDF 캔버스 좌표로 변환
  const convertEmuToPdfCoords = useCallback((emuOffset, emuSize, canvas) => {
    if (!slideSize || !canvas) return null;
    const { width: slideWidth, height: slideHeight } = slideSize;
    return {
      x: emuOffset[0] * (canvas.width / slideWidth),
      y: emuOffset[1] * (canvas.height / slideHeight),
      width: emuSize[0] * (canvas.width / slideWidth),
      height: emuSize[1] * (canvas.height / slideHeight)
    };
  }, [slideSize]);

  // PDF 텍스트에서 검색어의 모든 매칭 위치 찾기
  const findAllTextPositions = useCallback((searchText, items, viewport) => {
    if (!items.length || !searchText || !viewport) return [];

    const normalizedSearch = searchText.toLowerCase().replace(/\s+/g, '').trim();
    const matchingPositions = [];

    // 방법 1: 개별 아이템에서 직접 검색
    for (const item of items) {
      const itemTextNormalized = item.str.toLowerCase().replace(/\s+/g, '');
      const searchIndex = itemTextNormalized.indexOf(normalizedSearch);

      if (searchIndex !== -1) {
        const transform = item.transform;
        const [canvasX, canvasY] = viewport.convertToViewportPoint(transform[4], transform[5]);
        const fontHeight = Math.abs(transform[0]) || item.height || 20;
        const height = fontHeight * scaleRef.current;
        const itemWidth = item.width || 50;
        const totalChars = itemTextNormalized.length;

        // 여유 범위 적용 (0 ~ n 범위 내)
        const startIdx = Math.max(0, searchIndex - HIGHLIGHT_PADDING_START);
        const endIdx = Math.min(totalChars, searchIndex + normalizedSearch.length + HIGHLIGHT_PADDING_END);

        matchingPositions.push({
          x: canvasX + (itemWidth * startIdx / totalChars),
          y: canvasY - height,
          width: itemWidth * (endIdx - startIdx) / totalChars,
          height: height * 1.3
        });
      }
    }

    // 방법 2: 같은 줄 내 인접 아이템 병합 검색
    const lineGroups = groupTextItemsByLine(items);

    for (const line of lineGroups) {
      const lineInfo = line.map(item => {
        const [itemX] = viewport.convertToViewportPoint(item.transform[4], item.transform[5]);
        return { item, textNormalized: item.str.replace(/\s+/g, ''), startX: itemX, width: item.width || 50 };
      });

      const [, lineStartY] = viewport.convertToViewportPoint(line[0].transform[4], line[0].transform[5]);
      const lineText = lineInfo.map(info => info.textNormalized).join('').toLowerCase();
      let searchStart = 0;

      while ((searchStart = lineText.indexOf(normalizedSearch, searchStart)) !== -1) {
        let charCount = 0;
        let startItemInfo = null, startOffset = 0, endItemInfo = null, endOffset = 0;

        for (const info of lineInfo) {
          const itemChars = info.textNormalized.length;
          const itemEnd = charCount + itemChars;

          if (startItemInfo === null && searchStart < itemEnd) {
            startItemInfo = info;
            startOffset = searchStart - charCount;
          }
          if (searchStart + normalizedSearch.length <= itemEnd) {
            endItemInfo = info;
            endOffset = searchStart + normalizedSearch.length - charCount;
            break;
          }
          charCount = itemEnd;
        }

        if (startItemInfo && endItemInfo) {
          const fontHeight = Math.abs(startItemInfo.item.transform[0]) || startItemInfo.item.height || 20;
          const height = fontHeight * scaleRef.current;
          const startItemChars = startItemInfo.textNormalized.length || 1;
          const endItemChars = endItemInfo.textNormalized.length || 1;

          // 여유 범위 적용
          const paddedStartOffset = Math.max(0, startOffset - HIGHLIGHT_PADDING_START);
          const paddedEndOffset = startItemInfo === endItemInfo
            ? Math.min(startItemChars, endOffset + HIGHLIGHT_PADDING_END)
            : Math.min(endItemChars, endOffset + HIGHLIGHT_PADDING_END);

          const exactStartX = startItemInfo.startX + (startItemInfo.width * paddedStartOffset / startItemChars);
          const exactEndX = startItemInfo === endItemInfo
            ? startItemInfo.startX + (startItemInfo.width * paddedEndOffset / startItemChars)
            : endItemInfo.startX + (endItemInfo.width * paddedEndOffset / endItemChars);

          const newPos = { x: exactStartX, y: lineStartY - height, width: exactEndX - exactStartX, height: height * 1.3 };
          if (!matchingPositions.some(pos => Math.abs(pos.x - newPos.x) < 10 && Math.abs(pos.y - newPos.y) < 10)) {
            matchingPositions.push(newPos);
          }
        }
        searchStart += normalizedSearch.length;
      }
    }

    // 방법 3: 크로스라인 검색 (인접한 2줄을 합쳐서 검색) - 방법 1,2와 동일 우선순위
    if (lineGroups.length > 1) {
      for (let i = 0; i < lineGroups.length - 1; i++) {
        const line1 = lineGroups[i];
        const line2 = lineGroups[i + 1];

        const line1Info = line1.map(item => {
          const [itemX] = viewport.convertToViewportPoint(item.transform[4], item.transform[5]);
          return { item, textNormalized: item.str.replace(/\s+/g, ''), startX: itemX, width: item.width || 50 };
        });
        const line2Info = line2.map(item => {
          const [itemX] = viewport.convertToViewportPoint(item.transform[4], item.transform[5]);
          return { item, textNormalized: item.str.replace(/\s+/g, ''), startX: itemX, width: item.width || 50 };
        });

        const line1Text = line1Info.map(info => info.textNormalized).join('').toLowerCase();
        const line2Text = line2Info.map(info => info.textNormalized).join('').toLowerCase();
        const combinedText = line1Text + line2Text;

        let searchStart = 0;
        while ((searchStart = combinedText.indexOf(normalizedSearch, searchStart)) !== -1) {
          // 검색어가 두 줄에 걸쳐 있는지 확인
          if (searchStart < line1Text.length && searchStart + normalizedSearch.length > line1Text.length) {
            // 첫 번째 줄에서 매칭되는 부분
            const line1MatchStart = searchStart;
            const line1MatchEnd = line1Text.length;
            const line1MatchLen = line1MatchEnd - line1MatchStart;

            // 두 번째 줄에서 매칭되는 부분
            const line2MatchLen = normalizedSearch.length - line1MatchLen;

            // 크로스라인 하이라이트: 두 줄을 하나의 그룹으로 묶음
            const crossLineRects = [];

            // 첫 번째 줄 하이라이트 위치 계산
            let charCount = 0;
            let startItemInfo = null, startOffset = 0;
            for (const info of line1Info) {
              const itemChars = info.textNormalized.length;
              const itemEnd = charCount + itemChars;
              if (startItemInfo === null && line1MatchStart < itemEnd) {
                startItemInfo = info;
                startOffset = line1MatchStart - charCount;
                break;
              }
              charCount = itemEnd;
            }

            // startItemInfo를 못 찾으면 마지막 아이템 사용 (fallback)
            if (!startItemInfo && line1Info.length > 0) {
              startItemInfo = line1Info[line1Info.length - 1];
              startOffset = 0;
            }

            if (startItemInfo) {
              const [, line1Y] = viewport.convertToViewportPoint(startItemInfo.item.transform[4], startItemInfo.item.transform[5]);
              const fontHeight = Math.abs(startItemInfo.item.transform[0]) || startItemInfo.item.height || 20;
              const height = fontHeight * scaleRef.current;
              const startItemChars = startItemInfo.textNormalized.length || 1;

              // 여유 범위 적용
              const paddedStartOffset = Math.max(0, startOffset - HIGHLIGHT_PADDING_START);

              const exactStartX = startItemInfo.startX + (startItemInfo.width * paddedStartOffset / startItemChars);
              const lastItem = line1Info[line1Info.length - 1];
              const exactEndX = lastItem.startX + lastItem.width;

              crossLineRects.push({
                x: exactStartX,
                y: line1Y - height,
                width: Math.max(exactEndX - exactStartX, 10),
                height: height * 1.3
              });
            }

            // 두 번째 줄 하이라이트 위치 계산
            if (line2Info.length > 0) {
              const [, line2Y] = viewport.convertToViewportPoint(line2[0].transform[4], line2[0].transform[5]);
              const firstItem = line2Info[0];
              const fontHeight = Math.abs(firstItem.item.transform[0]) || firstItem.item.height || 20;
              const height = fontHeight * scaleRef.current;

              let endCharCount = 0;
              let endItemInfo = null, endOffset = 0;
              for (const info of line2Info) {
                const itemChars = info.textNormalized.length;
                const itemEnd = endCharCount + itemChars;
                if (line2MatchLen <= itemEnd) {
                  endItemInfo = info;
                  endOffset = line2MatchLen - endCharCount;
                  break;
                }
                endCharCount = itemEnd;
              }

              if (endItemInfo) {
                const endItemChars = endItemInfo.textNormalized.length || 1;

                // 여유 범위 적용
                const paddedEndOffset = Math.min(endItemChars, endOffset + HIGHLIGHT_PADDING_END);

                const exactEndX = endItemInfo.startX + (endItemInfo.width * paddedEndOffset / endItemChars);
                crossLineRects.push({
                  x: firstItem.startX,
                  y: line2Y - height,
                  width: Math.max(exactEndX - firstItem.startX, 10),
                  height: height * 1.3
                });
              }
            }

            // 크로스라인 그룹으로 추가 (rects 배열 포함)
            if (crossLineRects.length > 0) {
              // 첫 번째 rect를 기준으로 position 생성, 나머지는 extraRects에 저장
              const mainRect = crossLineRects[0];
              matchingPositions.push({
                ...mainRect,
                extraRects: crossLineRects.slice(1)
              });
            }
          }
          searchStart += normalizedSearch.length;
        }
      }
    }

    return matchingPositions;
  }, [groupTextItemsByLine]);

  // PDF 텍스트가 PPT shape 영역 내부에 있는지 확인
  const isInsidePptRect = useCallback((pos, pptRect, margin = 10) => {
    if (!pptRect) return false;
    const centerX = pos.x + pos.width / 2;
    const centerY = pos.y + pos.height / 2;
    return centerX >= pptRect.x - margin && centerX <= pptRect.x + pptRect.width + margin &&
           centerY >= pptRect.y - margin && centerY <= pptRect.y + pptRect.height + margin;
  }, []);

  // 하이라이트 선택 공통 로직
  const selectHighlightsForTypo = useCallback((typo, allPositions, pptRect, maxCount, usedPositions = [], existingHighlights = []) => {
    const threshold = pptRect ? Math.sqrt(pptRect.width ** 2 + pptRect.height ** 2) * 1.5 : Infinity;
    const pptCenterX = pptRect ? pptRect.x + pptRect.width / 2 : 0;
    const pptCenterY = pptRect ? pptRect.y + pptRect.height / 2 : 0;

    const addDistance = pos => ({
      ...pos,
      distance: Math.sqrt((pos.x + pos.width / 2 - pptCenterX) ** 2 + (pos.y + pos.height / 2 - pptCenterY) ** 2)
    });

    const insidePositions = pptRect ? allPositions.filter(pos => isInsidePptRect(pos, pptRect)) : [];
    const outsidePositions = pptRect ? allPositions.filter(pos => !isInsidePptRect(pos, pptRect)) : allPositions;
    const insideSorted = insidePositions.map(addDistance).sort((a, b) => a.distance - b.distance);
    const outsideSorted = outsidePositions.map(addDistance).sort((a, b) => a.distance - b.distance);

    const isUsedOrExists = pos =>
      usedPositions.some(u => Math.abs(u.x - pos.x) < 5 && Math.abs(u.y - pos.y) < 5) ||
      existingHighlights.some(h => Math.abs(h.x - pos.x) < 5 && Math.abs(h.y - pos.y) < 5);

    const selected = [];
    let count = 0;

    // 1. 내부 위치 우선
    for (const pos of insideSorted) {
      if (count >= maxCount) break;
      if (!isUsedOrExists(pos)) {
        selected.push(pos);
        usedPositions.push(pos);
      }
      count++;
    }

    // 2. 외부 위치 (임계값 내)
    if (count < maxCount) {
      for (const pos of outsideSorted) {
        if (count >= maxCount) break;
        if (pos.distance > threshold) break;
        if (!isUsedOrExists(pos)) {
          selected.push(pos);
          usedPositions.push(pos);
        }
        count++;
      }
    }

    // 3. Fallback: 아무것도 못 찾으면 가장 가까운 1개
    if (count === 0 && allPositions.length > 0) {
      const allSorted = allPositions.map(addDistance).sort((a, b) => a.distance - b.distance);
      const closest = allSorted[0];
      if (!isUsedOrExists(closest)) {
        selected.push(closest);
        usedPositions.push(closest);
      }
    }

    return selected;
  }, [isInsidePptRect]);

  // ========== PDF 렌더링 ==========

  const renderPage = useCallback(async (num, force = false) => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current) return;
    if (!force && (lastRenderedPageRef.current === num || renderingPageRef.current === num)) return;

    if (isRenderingRef.current && renderTaskRef.current) {
      try { renderTaskRef.current.cancel(); await new Promise(r => setTimeout(r, 50)); } catch {}
    }

    isRenderingRef.current = true;
    renderingPageRef.current = num;
    lastRenderedPageRef.current = null;

    try {
      const page = await pdfDoc.getPage(num);
      if (!canvasRef.current || !containerRef.current) return;

      const canvas = canvasRef.current;
      const container = containerRef.current;
      const defaultViewport = page.getViewport({ scale: 1 });
      const fitScale = Math.min((container.clientWidth - 40) / defaultViewport.width, (container.clientHeight - 40) / defaultViewport.height, 2);

      scaleRef.current = fitScale;
      setScale(fitScale);

      const viewport = page.getViewport({ scale: fitScale });
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      setCurrentViewport(viewport);

      const renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport });
      renderTaskRef.current = renderTask;
      await renderTask.promise;
      renderTaskRef.current = null;

      const textContent = await page.getTextContent();
      setTextItems(textContent.items);
      lastRenderedPageRef.current = num;
      renderingPageRef.current = null;
    } catch (error) {
      if (error.name !== 'RenderingCancelledException' && !error.message?.includes('multiple render()')) {
        console.error('PDF 렌더링 에러:', error);
      }
    } finally {
      isRenderingRef.current = false;
    }
  }, [pdfDoc]);

  // ========== Effects ==========

  useEffect(() => {
    if (!isOpen || pdfUrlProp || localPdfUrl || !selectedPpt) return;
    fetchPdfUrl(selectedPpt).then(url => url && setLocalPdfUrl(url)).catch(() => {});
  }, [isOpen, pdfUrlProp, localPdfUrl, selectedPpt]);

  useEffect(() => {
    const effectivePdfUrl = pdfUrlProp || localPdfUrl;
    if (!isOpen || !effectivePdfUrl) return;
    const targetPage = currentSlideIndex + 1;

    if (cachedPdfDoc) {
      setTotalPages(cachedPdfDoc.numPages);
      if (pageNum !== targetPage) setPageNum(targetPage);
      return;
    }

    setIsLoading(true);
    pdfjsLib.getDocument(effectivePdfUrl).promise.then(pdf => {
      setCachedPdfDoc?.(pdf);
      setLocalPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setPageNum(targetPage);
    }).catch(() => {}).finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pdfUrlProp, localPdfUrl, cachedPdfDoc, setCachedPdfDoc, currentSlideIndex]);

  useEffect(() => {
    if (pdfDoc && pageNum && isOpen && isContainerReady) renderPage(pageNum);
  }, [pdfDoc, pageNum, renderPage, isOpen, isContainerReady]);

  useEffect(() => {
    if (pdfDoc) setPageNum(currentSlideIndex + 1);
  }, [currentSlideIndex, pdfDoc]);

  useEffect(() => {
    if (!isOpen) {
      setIsContainerReady(false);
      lastRenderedPageRef.current = null;
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
      return;
    }
    const checkContainer = () => {
      if (containerRef.current?.clientWidth > 0 && containerRef.current?.clientHeight > 0) {
        setIsContainerReady(true);
      } else {
        requestAnimationFrame(checkContainer);
      }
    };
    requestAnimationFrame(checkContainer);
  }, [isOpen]);

  // ========== 하이라이트 기능 ==========

  // 노란색 하이라이트 (클릭 시 임시)
  const findAndHighlightText = useCallback((typo) => {
    if (!textItems.length || !typo || !canvasRef.current || !currentViewport) return;

    const originalText = typo.original;
    const pptRect = typo.offset && typo.size && slideSize
      ? convertEmuToPdfCoords(typo.offset, typo.size, canvasRef.current) : null;
    const sameTextCount = typo.same_text_count_in_shape ||
      (typo.text?.match(new RegExp(originalText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'))?.length || 1);

    const allPositions = findAllTextPositions(originalText, textItems, currentViewport);
    const highlights = selectHighlightsForTypo(typo, allPositions, pptRect, sameTextCount);

    setHighlightRects(highlights);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => {
      setHighlightRects([]);
      highlightTimerRef.current = null;
    }, 1500);
  }, [textItems, currentViewport, slideSize, convertEmuToPdfCoords, findAllTextPositions, selectHighlightsForTypo]);

  // 빨간색 하이라이트 (선택된 오타)
  const calculateSelectedHighlights = useCallback(() => {
    if (!canvasRef.current || !currentViewport || !textItems.length || selectedTypos.length === 0) {
      setSelectedHighlights([]);
      return;
    }

    const canvas = canvasRef.current;
    const highlights = [];
    const usedPositionsByShapeAndText = {};

    selectedTypos.forEach(typoIndex => {
      const typo = currentTypos[typoIndex];
      if (!typo) return;

      const originalText = typo.original;
      const shapePath = typo.shape_path || 'default';
      const key = `${shapePath}_${originalText}`;
      if (!usedPositionsByShapeAndText[key]) usedPositionsByShapeAndText[key] = [];

      const pptRect = typo.offset && typo.size && slideSize
        ? convertEmuToPdfCoords(typo.offset, typo.size, canvas) : null;
      const sameTextCount = typo.same_text_count_in_shape ||
        (typo.text?.match(new RegExp(originalText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'))?.length || 1);

      const allPositions = findAllTextPositions(originalText, textItems, currentViewport);
      const selected = selectHighlightsForTypo(
        typo, allPositions, pptRect, sameTextCount,
        usedPositionsByShapeAndText[key], highlights
      );

      selected.forEach(pos => highlights.push({ typoIndex, ...pos }));
    });

    setSelectedHighlights(highlights);
  }, [selectedTypos, currentTypos, textItems, currentViewport, findAllTextPositions, convertEmuToPdfCoords, slideSize, selectHighlightsForTypo]);

  useEffect(() => {
    calculateSelectedHighlights();
  }, [selectedTypos, textItems, calculateSelectedHighlights]);

  // ========== 이벤트 핸들러 ==========

  const handleTypoClick = (typo, index) => {
    if (!selectedTypos.includes(index)) findAndHighlightText(typo);
    toggleSlideTypoSelection(currentSlideIndex, index);
  };

  const goToPrevSlide = () => currentSlideIndex > 0 && goToSlide(currentSlideIndex - 1);
  const goToNextSlide = () => currentSlideIndex < slides.length - 1 && goToSlide(currentSlideIndex + 1);

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel - ZOOM_STEP, MIN_ZOOM);
    setZoomLevel(newZoom);
    if (newZoom === MIN_ZOOM) setPanOffset({ x: 0, y: 0 });
  };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = zoomContainerRef.current;
    if (!container || !canvasRef.current) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const zoomDelta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoomLevel + zoomDelta));

    if (newZoom !== zoomLevel) {
      const imageX = (mouseX - panOffset.x) / zoomLevel;
      const imageY = (mouseY - panOffset.y) / zoomLevel;
      setZoomLevel(newZoom);
      setPanOffset(newZoom === MIN_ZOOM ? { x: 0, y: 0 } : { x: mouseX - imageX * newZoom, y: mouseY - imageY * newZoom });
    }
  }, [zoomLevel, panOffset]);

  const handleMouseDown = (e) => {
    if (zoomLevel > MIN_ZOOM) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      e.preventDefault();
    }
  };

  const handleTouchStart = (e) => {
    if (zoomLevel > MIN_ZOOM && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - panOffset.x, y: e.touches[0].clientY - panOffset.y });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const canvas = canvasRef.current;
    if (!canvas) { setPanOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }); return; }

    const scaledWidth = canvas.width * zoomLevel;
    const scaledHeight = canvas.height * zoomLevel;
    const limit = 0.3;
    const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

    setPanOffset({
      x: clamp(e.clientX - dragStart.x, scaledWidth * limit - scaledWidth, scaledWidth * limit),
      y: clamp(e.clientY - dragStart.y, scaledHeight * limit - scaledHeight, scaledHeight * limit)
    });
  }, [isDragging, dragStart, zoomLevel]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    if (!canvas) { setPanOffset({ x: touch.clientX - dragStart.x, y: touch.clientY - dragStart.y }); return; }

    const scaledWidth = canvas.width * zoomLevel;
    const scaledHeight = canvas.height * zoomLevel;
    const limit = 0.3;
    const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

    setPanOffset({
      x: clamp(touch.clientX - dragStart.x, scaledWidth * limit - scaledWidth, scaledWidth * limit),
      y: clamp(touch.clientY - dragStart.y, scaledHeight * limit - scaledHeight, scaledHeight * limit)
    });
  }, [isDragging, dragStart, zoomLevel]);

  const handleTouchEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  useEffect(() => {
    const container = zoomContainerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel, isContainerReady, isLoading]);

  useEffect(() => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, [currentSlideIndex]);

  // 온보딩 단계 정의
  const onboardingSteps = [
    {
      target: 'typo-list',
      title: t('onboarding.typo.step1Title'),
      description: t('onboarding.typo.step1Desc'),
      position: 'right'
    },
    {
      target: 'typo-card',
      title: t('onboarding.typo.step2Title'),
      description: t('onboarding.typo.step2Desc'),
      position: 'right'
    },
    {
      target: 'pdf-viewer',
      title: t('onboarding.typo.step3Title'),
      description: t('onboarding.typo.step3Desc'),
      position: 'left'
    },
    {
      target: 'zoom-controls',
      title: t('onboarding.typo.step4Title'),
      description: t('onboarding.typo.step4Desc'),
      position: 'center'
    },
    {
      target: 'slide-nav',
      title: t('onboarding.typo.step5Title'),
      description: t('onboarding.typo.step5Desc'),
      position: 'bottom'
    },
    {
      target: 'footer-buttons',
      title: t('onboarding.typo.step6Title'),
      description: t('onboarding.typo.step6Desc'),
      position: 'top'
    }
  ];

  // 모달이 열릴 때 온보딩 자동 시작 (처음 사용자만)
  useEffect(() => {
    if (isOpen) {
      const userId = auth.currentUser?.uid;
      if (userId) {
        const completed = localStorage.getItem(`onboarding_typo_completed_${userId}`);
        if (!completed) {
          setShowOnboarding(true);
          setOnboardingStep(0);
        }
      }
    }
  }, [isOpen]);

  const handleNextStep = () => {
    if (onboardingStep < onboardingSteps.length - 1) {
      setOnboardingStep(prev => prev + 1);
    } else {
      setShowOnboarding(false);
      setOnboardingStep(0);
      const userId = auth.currentUser?.uid;
      if (userId) {
        localStorage.setItem(`onboarding_typo_completed_${userId}`, 'true');
      }
    }
  };

  const handlePrevStep = () => {
    if (onboardingStep > 0) {
      setOnboardingStep(prev => prev - 1);
    }
  };

  const handleSkipOnboarding = () => {
    setShowOnboarding(false);
    setOnboardingStep(0);
    const userId = auth.currentUser?.uid;
    if (userId) {
      localStorage.setItem(`onboarding_typo_completed_${userId}`, 'true');
    }
  };

  const getTotalSelectedTypos = () => Object.values(selectedSlideTypos).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  const getTotalTyposCount = () => Object.values(slideTypoResults).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  const isAllSelected = () => { const total = getTotalTyposCount(); return total > 0 && total === getTotalSelectedTypos(); };

  const handleSelectAll = () => {
    Object.keys(slideTypoResults).forEach(idx => {
      const typos = slideTypoResults[idx] || [];
      const selected = selectedSlideTypos[idx] || [];
      typos.forEach((_, i) => { if (!selected.includes(i)) toggleSlideTypoSelection(parseInt(idx), i); });
    });
  };

  const handleDeselectAll = () => {
    Object.keys(selectedSlideTypos).forEach(idx => {
      selectedSlideTypos[idx]?.forEach(i => toggleSlideTypoSelection(parseInt(idx), i));
    });
  };

  const handleReset = () => {
    handleDeselectAll();
    Object.keys(slideTypoResults).forEach(idx => {
      slideTypoResults[idx]?.forEach((typo, i) => updateEditedSuggestion('slide', `${idx}_${i}`, typo.suggestion || ''));
    });
  };

  // ========== 렌더링 ==========

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000
    }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes blink { 0% { opacity: 0.4; } 100% { opacity: 0.8; } }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}} />

      <div id="typo-result-modal" style={{
        background: '#ffffff', borderRadius: isMobile ? '12px' : '20px',
        width: isMobile ? '100vw' : '95vw', maxWidth: isMobile ? '100vw' : '1400px', height: isMobile ? '100vh' : '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)', position: 'relative'
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: isMobile ? '12px 16px' : '16px 24px', borderBottom: '1px solid #e5e7eb', background: '#ffffff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
            {isMobile && (
              <button onClick={() => setShowMobileSidebar(!showMobileSidebar)} style={{
                background: showMobileSidebar ? '#1A62EA' : 'transparent', border: 'none', cursor: 'pointer',
                padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: showMobileSidebar ? '#ffffff' : '#6b7280', transition: 'all 0.2s ease'
              }}><FaBars size={18} /></button>
            )}
            <h2 style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              {t('typoResultModal.title')}
            </h2>
            <span style={{
              background: '#fee2e2', color: '#dc2626',
              padding: isMobile ? '3px 8px' : '4px 10px', borderRadius: '12px', fontSize: isMobile ? '11px' : '12px', fontWeight: '600'
            }}>{t('typoResultModal.totalTypos', { count: getTotalTyposCount() })}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* 도움말 버튼 */}
            <button
              onClick={() => setShowOnboarding(true)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#EDF4FE'; e.currentTarget.style.color = '#1A62EA'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
              title={t('onboarding.helpButton')}
            >
              <FaQuestionCircle size={isMobile ? 18 : 20} />
            </button>
            {/* 닫기 버튼 */}
            <button onClick={onClose} style={{
              background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', transition: 'all 0.2s ease'
            }} onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#1f2937'; }}
               onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}>
              <FaTimes size={isMobile ? 18 : 20} />
            </button>
          </div>
        </div>

        {/* 메인 콘텐츠 */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
          {/* 왼쪽: 오타 목록 */}
          <div style={{
            width: isMobile ? '85%' : '420px', maxWidth: isMobile ? '320px' : '420px',
            borderRight: isMobile ? 'none' : '1px solid #e5e7eb',
            display: 'flex', flexDirection: 'column', background: '#ffffff',
            ...(isMobile && {
              position: 'absolute', top: 0, left: showMobileSidebar ? 0 : '-100%', height: '100%', zIndex: 100,
              boxShadow: showMobileSidebar ? '4px 0 20px rgba(0, 0, 0, 0.15)' : 'none', transition: 'left 0.3s ease'
            })
          }}>
            <div id="typo-list" style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', background: '#EDF4FE', position: 'relative' }}>
              {isMobile && (
                <button onClick={() => setShowMobileSidebar(false)} style={{
                  position: 'absolute', top: '12px', right: '12px', background: 'transparent', border: 'none',
                  cursor: 'pointer', padding: '6px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280'
                }}><FaTimes size={16} /></button>
              )}
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', margin: 0, marginBottom: '6px' }}>
                {t('typoResultModal.slideTypos', { num: currentSlideIndex + 1, count: currentTypos.length })}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ background: '#fee2e2', color: '#dc2626', padding: '3px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>
                  {t('typoResultModal.totalTypos', { count: currentTypos.length })}
                </span>
                {getTotalSelectedTypos() > 0 && (
                  <span style={{ background: '#d4edda', color: '#155724', padding: '3px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>
                    {t('typoResultModal.selected')} {getTotalSelectedTypos()}
                  </span>
                )}
              </div>
            </div>

            <div id="typo-card" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#EDF4FE' }}>
              {/* 검사 안 된 슬라이드 */}
              {!slideTypoCheckStatus?.[currentSlideIndex]?.hasBeenChecked ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '16px' }}>{t('typoResultModal.notCheckedYet')}</div>
                  {checkSlideTypos && (
                    <button
                      onClick={() => checkSlideTypos(currentSlideIndex, { silent: true })}
                      disabled={slideTypoCheckStatus?.[currentSlideIndex]?.checking}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        background: slideTypoCheckStatus?.[currentSlideIndex]?.checking ? '#9ca3af' : '#1A62EA',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: slideTypoCheckStatus?.[currentSlideIndex]?.checking ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {slideTypoCheckStatus?.[currentSlideIndex]?.checking ? (
                        <>
                          <span style={{
                            display: 'inline-block', width: '14px', height: '14px',
                            border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white',
                            borderRadius: '50%', animation: 'spin 1s linear infinite'
                          }} />
                          {t('typoViewer.checking')}
                        </>
                      ) : (
                        <>
                          <FaSpellCheck size={14} />
                          {t('typoViewer.checkSlide')}
                        </>
                      )}
                    </button>
                  )}
                </div>
              ) : currentTypos.length === 0 ? (
                /* 검사 완료 + 오타 없음 */
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>✨</div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{t('typoResultModal.noTyposInSlide')}</div>
                </div>
              ) : (
                currentTypos.map((typo, index) => {
                  const isSelected = selectedTypos.includes(index);
                  const editedValue = getEditedSuggestion('slide', `${currentSlideIndex}_${index}`, typo.suggestion) || '';
                  return (
                    <div key={index} onClick={() => handleTypoClick(typo, index)} style={{
                      background: isSelected ? '#dbeafe' : '#ffffff',
                      border: isSelected ? '2px solid #1d4ed8' : '1px solid #e5e7eb',
                      borderRadius: '12px', padding: '16px', marginBottom: '12px', cursor: 'pointer', transition: 'all 0.2s ease'
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ backgroundColor: '#f8f9fa', padding: '8px 12px', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                          <div style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500', marginBottom: '4px' }}>{t('typoCheckResults.context')}</div>
                          <div style={{ fontSize: '14px', color: '#495057', lineHeight: '1.5', wordBreak: 'break-word' }}>
                            {typo.text || typo.context || `[${typo.original}]`}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: '#dc3545', marginBottom: '4px', fontWeight: '600' }}>{t('typoCheckResults.original')}</div>
                          <div onClick={(e) => e.stopPropagation()} style={{
                            padding: '8px 10px', border: '1px solid #fecaca', borderRadius: '6px',
                            fontSize: '14px', color: '#495057', background: '#fff5f5', wordBreak: 'break-word', minHeight: '36px', boxSizing: 'border-box'
                          }}>{typo.original}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#28a745', marginBottom: '4px', fontWeight: '600' }}>{t('typoCheckResults.suggestion')}</div>
                          <input type="text" value={editedValue}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newValue = e.target.value;
                              updateEditedSuggestion('slide', `${currentSlideIndex}_${index}`, newValue);
                              if (newValue !== (typo.suggestion || '') && !isSelected) toggleSlideTypoSelection(currentSlideIndex, index);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '100%', padding: '8px 10px', border: '1px solid #d4edda', borderRadius: '6px',
                              fontSize: '14px', color: '#155724', background: '#f8fff9', boxSizing: 'border-box', minHeight: '36px'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {isMobile && showMobileSidebar && (
            <div onClick={() => setShowMobileSidebar(false)} style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.3)', zIndex: 99
            }} />
          )}

          {/* 오른쪽: PDF 뷰어 */}
          <div id="pdf-viewer" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#ffffff', position: 'relative' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px', borderBottom: '1px solid #e5e7eb', background: '#ffffff'
            }}>
              {/* 왼쪽 공간 (균형 맞추기) */}
              <div style={{ flex: 1 }}></div>

              {/* 중앙: 슬라이드 네비게이션 */}
              <div id="slide-nav" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <button onClick={goToPrevSlide} disabled={currentSlideIndex === 0} style={{
                  background: 'transparent', border: 'none', cursor: currentSlideIndex === 0 ? 'not-allowed' : 'pointer',
                  padding: '6px 10px', display: 'flex', alignItems: 'center', color: currentSlideIndex === 0 ? '#d1d5db' : '#6b7280', fontSize: '20px'
                }}><FaChevronLeft /></button>
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#374151' }}>
                  {currentSlideIndex + 1} / {slides?.length || totalPages}
                </span>
                <button onClick={goToNextSlide} disabled={currentSlideIndex >= (slides?.length || totalPages) - 1} style={{
                  background: 'transparent', border: 'none', cursor: currentSlideIndex >= (slides?.length || totalPages) - 1 ? 'not-allowed' : 'pointer',
                  padding: '6px 10px', display: 'flex', alignItems: 'center', color: currentSlideIndex >= (slides?.length || totalPages) - 1 ? '#d1d5db' : '#6b7280', fontSize: '20px'
                }}><FaChevronRight /></button>
              </div>

              {/* 오른쪽 공간 (균형 맞추기) */}
              <div style={{ flex: 1 }}></div>
            </div>

            <div ref={containerRef} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '20px', position: 'relative', overflow: 'hidden'
            }}>
              {(isLoading || !isContainerReady) ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#6b7280' }}>
                  <div style={{
                    width: '40px', height: '40px', border: '3px solid #e5e7eb', borderTop: '3px solid #1A62EA',
                    borderRadius: '50%', animation: 'spin 1s linear infinite'
                  }} />
                  <span>PDF 로딩 중...</span>
                </div>
              ) : (
                <div ref={zoomContainerRef} onMouseDown={handleMouseDown} onTouchStart={handleTouchStart} style={{
                  position: 'relative', cursor: zoomLevel > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  userSelect: 'none', touchAction: zoomLevel > MIN_ZOOM ? 'none' : 'auto'
                }}>
                  <div style={{
                    transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                    transformOrigin: '0 0', transition: isDragging ? 'none' : 'transform 0.1s ease-out', position: 'relative'
                  }}>
                    <canvas ref={canvasRef} style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)', borderRadius: '8px', display: 'block' }} />
                    {highlightRects.flatMap((rect, i) => {
                      const rects = [rect, ...(rect.extraRects || [])];
                      return rects.map((r, j) => (
                        <div key={`${i}-${j}`} style={{
                          position: 'absolute', left: r.x, top: r.y, width: r.width, height: r.height,
                          backgroundColor: 'rgba(255, 255, 0, 0.6)', border: '2px solid rgba(255, 200, 0, 0.9)',
                          borderRadius: '4px', pointerEvents: 'none', animation: 'blink 0.3s ease-in-out infinite alternate'
                        }} />
                      ));
                    })}
                    {selectedHighlights.flatMap((h, i) => {
                      const rects = [h, ...(h.extraRects || [])];
                      return rects.map((r, j) => {
                        // 중심 기준 확장
                        const expandedX = r.x - (r.width * HIGHLIGHT_EXPAND_RATIO / 2);
                        const expandedY = r.y - (r.height * HIGHLIGHT_EXPAND_RATIO / 2);
                        const expandedWidth = r.width * (1 + HIGHLIGHT_EXPAND_RATIO);
                        const expandedHeight = r.height * (1 + HIGHLIGHT_EXPAND_RATIO);
                        return (
                          <div key={`${i}-${j}`} style={{
                            position: 'absolute', left: expandedX, top: expandedY, width: expandedWidth, height: expandedHeight,
                            border: '2px solid #dc2626', borderRadius: '4px', pointerEvents: 'none',
                            boxShadow: '0 0 0 1px rgba(220, 38, 38, 0.3)'
                          }} />
                        );
                      });
                    })}
                  </div>
                </div>
              )}

              <div id="zoom-controls" style={{
                position: 'absolute', bottom: '30px', right: '30px',
                display: 'flex', flexDirection: 'column', gap: '8px',
                background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', padding: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}>
                <button onClick={handleZoomIn} disabled={zoomLevel >= MAX_ZOOM} style={{
                  width: '40px', height: '40px', borderRadius: '8px', border: '1px solid #e5e7eb',
                  background: zoomLevel >= MAX_ZOOM ? '#f3f4f6' : '#ffffff', cursor: zoomLevel >= MAX_ZOOM ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: zoomLevel >= MAX_ZOOM ? '#9ca3af' : '#374151', transition: 'all 0.2s ease'
                }}><FaSearchPlus size={16} /></button>
                <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', padding: '4px 0' }}>
                  {Math.round(zoomLevel * 100)}%
                </div>
                <button onClick={handleZoomOut} disabled={zoomLevel <= MIN_ZOOM} style={{
                  width: '40px', height: '40px', borderRadius: '8px', border: '1px solid #e5e7eb',
                  background: zoomLevel <= MIN_ZOOM ? '#f3f4f6' : '#ffffff', cursor: zoomLevel <= MIN_ZOOM ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: zoomLevel <= MIN_ZOOM ? '#9ca3af' : '#374151', transition: 'all 0.2s ease'
                }}><FaSearchMinus size={16} /></button>
              </div>
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div id="footer-buttons" style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? '8px' : '0',
          padding: isMobile ? '12px 16px' : '16px 24px', borderTop: '1px solid #e5e7eb', background: '#ffffff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flex: isMobile ? 1 : 'none' }}>
            <button onClick={handleReset} disabled={getTotalSelectedTypos() === 0} style={{
              flex: isMobile ? 1 : 'none', padding: isMobile ? '10px 12px' : '12px 20px', borderRadius: isMobile ? '8px' : '10px',
              border: '1px solid #d1d5db', background: '#ffffff', color: getTotalSelectedTypos() === 0 ? '#9ca3af' : '#374151',
              fontSize: isMobile ? '13px' : '14px', fontWeight: '600', cursor: getTotalSelectedTypos() === 0 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s ease'
            }}><FaUndo size={isMobile ? 12 : 14} />{t('typoResultModal.resetAll')}</button>
            <button onClick={isAllSelected() ? handleDeselectAll : handleSelectAll} disabled={getTotalTyposCount() === 0} style={{
              flex: isMobile ? 1 : 'none', padding: isMobile ? '10px 12px' : '12px 20px', borderRadius: isMobile ? '8px' : '10px',
              border: '1px solid #d1d5db', background: isAllSelected() ? '#fee2e2' : '#f0fdf4',
              color: isAllSelected() ? '#dc2626' : '#166534', fontSize: isMobile ? '13px' : '14px', fontWeight: '600',
              cursor: getTotalTyposCount() === 0 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s ease'
            }}>{isAllSelected() ? t('typoResultModal.deselectAll') : t('typoResultModal.selectAll')}</button>
            {/* 임시저장 버튼 */}
            {onSaveDraft && (
              <button onClick={onSaveDraft} disabled={!hasUnsavedChanges} style={{
                flex: isMobile ? 1 : 'none', padding: isMobile ? '10px 12px' : '12px 20px', borderRadius: isMobile ? '8px' : '10px',
                border: '1px solid #d1d5db', background: hasUnsavedChanges ? '#EDF4FE' : '#ffffff',
                color: !hasUnsavedChanges ? '#9ca3af' : '#1A62EA', fontSize: isMobile ? '13px' : '14px', fontWeight: '600',
                cursor: !hasUnsavedChanges ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s ease'
              }}><FaSave size={isMobile ? 12 : 14} />{t('draft.saveButton')}</button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flex: isMobile ? 1 : 'none' }}>
            <button onClick={onClose} style={{
              flex: isMobile ? 1 : 'none', padding: isMobile ? '10px 12px' : '12px 24px', borderRadius: isMobile ? '8px' : '10px',
              border: '1px solid #d1d5db', background: '#ffffff', color: '#6b7280',
              fontSize: isMobile ? '13px' : '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease'
            }}>{t('common.close')}</button>
            <button onClick={() => applySlideTypoCorrections?.()} disabled={getTotalSelectedTypos() === 0 || applyingTypoCorrections} style={{
              flex: isMobile ? 1 : 'none', padding: isMobile ? '10px 12px' : '12px 28px', borderRadius: isMobile ? '8px' : '10px',
              border: 'none', background: getTotalSelectedTypos() === 0 || applyingTypoCorrections ? '#9ca3af' : '#1A62EA', color: '#ffffff',
              fontSize: isMobile ? '13px' : '14px', fontWeight: '600',
              cursor: getTotalSelectedTypos() === 0 || applyingTypoCorrections ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s ease'
            }}>
              {applyingTypoCorrections ? (
                <><span style={{
                  display: 'inline-block', width: '14px', height: '14px',
                  border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white',
                  borderRadius: '50%', animation: 'spin 1s linear infinite'
                }} />{t('typoResultModal.modifying')}</>
              ) : t('resultModal.applyChange')}
            </button>
          </div>
        </div>

        {/* 온보딩 오버레이 */}
        {showOnboarding && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 3000
          }}>
            {/* 배경 오버레이 (클릭 감지용, 투명) */}
            <div
              onClick={handleSkipOnboarding}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'transparent',
                zIndex: 3001
              }}
            />

            {/* 하이라이트 영역 */}
            {(() => {
              const targetElement = document.getElementById(onboardingSteps[onboardingStep]?.target);
              if (!targetElement) return null;

              const rect = targetElement.getBoundingClientRect();
              const modalElement = document.getElementById('typo-result-modal');
              const modalRect = modalElement?.getBoundingClientRect() || { left: 0, top: 0 };

              const relativeRect = {
                left: rect.left - modalRect.left,
                top: rect.top - modalRect.top,
                width: rect.width,
                height: rect.height
              };

              const step = onboardingSteps[onboardingStep];
              let tooltipStyle = {
                position: 'absolute',
                background: '#ffffff',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                zIndex: 3003,
                maxWidth: '320px',
                minWidth: '280px'
              };

              // 툴팁 위치 계산
              switch (step.position) {
                case 'right':
                  tooltipStyle.left = relativeRect.left + relativeRect.width + 16;
                  tooltipStyle.top = relativeRect.top + relativeRect.height / 2;
                  tooltipStyle.transform = 'translateY(-50%)';
                  break;
                case 'left':
                  tooltipStyle.right = `calc(100% - ${relativeRect.left - 16}px)`;
                  tooltipStyle.top = relativeRect.top + relativeRect.height / 2;
                  tooltipStyle.transform = 'translateY(-50%)';
                  break;
                case 'bottom':
                  tooltipStyle.left = relativeRect.left + relativeRect.width / 2;
                  tooltipStyle.top = relativeRect.top + relativeRect.height + 16;
                  tooltipStyle.transform = 'translateX(-50%)';
                  break;
                case 'top':
                  tooltipStyle.left = relativeRect.left + relativeRect.width / 2;
                  tooltipStyle.bottom = `calc(100% - ${relativeRect.top - 16}px)`;
                  tooltipStyle.transform = 'translateX(-50%)';
                  break;
                case 'center':
                  tooltipStyle.left = '50%';
                  tooltipStyle.top = '50%';
                  tooltipStyle.transform = 'translate(-50%, -50%)';
                  break;
                default:
                  tooltipStyle.left = relativeRect.left + relativeRect.width + 16;
                  tooltipStyle.top = relativeRect.top;
              }

              return (
                <>
                  {/* 하이라이트 박스 */}
                  <div style={{
                    position: 'absolute',
                    left: relativeRect.left - 4,
                    top: relativeRect.top - 4,
                    width: relativeRect.width + 8,
                    height: relativeRect.height + 8,
                    border: '3px solid #1A62EA',
                    borderRadius: '8px',
                    zIndex: 3002,
                    pointerEvents: 'none',
                    background: 'transparent',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)'
                  }} />

                  {/* 툴팁 */}
                  <div style={tooltipStyle}>
                    {/* 단계 표시 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px'
                    }}>
                      <span style={{
                        background: '#1A62EA',
                        color: '#ffffff',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {onboardingStep + 1} / {onboardingSteps.length}
                      </span>
                    </div>

                    {/* 제목 */}
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1f2937',
                      margin: '0 0 8px 0'
                    }}>
                      {step.title}
                    </h4>

                    {/* 설명 */}
                    <p style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: '0 0 20px 0',
                      lineHeight: '1.6'
                    }}>
                      {step.description}
                    </p>

                    {/* 버튼 영역 */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <button
                        onClick={handleSkipOnboarding}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#9ca3af',
                          fontSize: '13px',
                          cursor: 'pointer',
                          padding: '8px 12px'
                        }}
                      >
                        {t('onboarding.skip')}
                      </button>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        {onboardingStep > 0 && (
                          <button
                            onClick={handlePrevStep}
                            style={{
                              background: '#f3f4f6',
                              border: 'none',
                              color: '#374151',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            {t('onboarding.prev')}
                          </button>
                        )}
                        <button
                          onClick={handleNextStep}
                          style={{
                            background: '#1A62EA',
                            border: 'none',
                            color: '#ffffff',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {onboardingStep === onboardingSteps.length - 1
                            ? t('onboarding.finish')
                            : t('onboarding.next')
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default TypoResultModal;
